import React, { memo, useState, useEffect, useCallback } from 'react';
import useRefSize from '../_hooks/useRefSize';
import useScreenSize from '../_hooks/useScreensize';
import FlipbookLoader from './flipbook-loader';
import { cn } from '../_lib/utils';
import { TransformComponent } from 'react-zoom-pan-pinch';
import screenfull from 'screenfull';

const Flipbook = memo(({ viewerStates, setViewerStates, flipbookRef, pdfDetails }) => {
  const { ref, width, height, refreshSize } = useRefSize();
  const { width: screenWidth } = useScreenSize();
  const isMobile = screenWidth < 768;
  const [scale, setScale] = useState(1);
  const [wrapperCss, setWrapperCss] = useState({});
  const [viewRange, setViewRange] = useState([0, 4]);
  
  // Detect iOS
  const [isIOS] = useState(() => {
    if (typeof window === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  });

  useEffect(() => {
    if (pdfDetails) {
      // Prefer measured container size, but fall back to viewport so the book can fill the screen
      let containerWidth = (width && width > 0) ? width : (window?.innerWidth || 1024);
      let containerHeight = (height && height > 0) ? height : (window?.innerHeight || 768);
      
      // On iOS, account for browser UI that might appear/disappear
      if (isIOS) {
        // Use smaller viewport on iOS to avoid scrolling issues
        containerHeight = Math.max(containerHeight * 0.9, 500);
      }
      
      const calculatedScale = Math.min(containerWidth / (2 * pdfDetails.width), containerHeight / pdfDetails.height) || 1;
      setScale(calculatedScale);
      
      // Set wrapper to the natural book size (two pages side-by-side) and let the container center/scale it
      const wrapperWidth = pdfDetails.width * calculatedScale * 2;
      const wrapperHeight = pdfDetails.height * calculatedScale;
      
      setWrapperCss({ 
        width: `${wrapperWidth}px`, 
        height: `${wrapperHeight}px`, 
        transformOrigin: 'center', 
        transform: 'none',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none'
      });
    }
  }, [pdfDetails, width, height, isIOS]);

  const shrinkPageLoadingRange = useCallback(() => {
    setViewRange([Math.max(viewerStates.currentPageIndex - 2, 0), Math.min(viewerStates.currentPageIndex + 2, pdfDetails.totalPages)]);
  }, [viewerStates.currentPageIndex, pdfDetails?.totalPages]);

  const handleFullscreenChange = useCallback(() => {
    shrinkPageLoadingRange();
    // Add delay on iOS to allow for proper layout
    if (isIOS) {
      setTimeout(() => {
        refreshSize();
      }, 100);
    } else {
      refreshSize();
    }
  }, [shrinkPageLoadingRange, refreshSize, isIOS]);

  useEffect(() => {
    // screenfull is not supported on iOS and may not have event handlers
    if (screenfull && typeof screenfull.on === 'function') {
      try {
        screenfull.on('change', handleFullscreenChange);
        return () => {
          if (typeof screenfull.off === 'function') {
            screenfull.off('change', handleFullscreenChange);
          }
        };
      } catch (error) {
        console.warn('Fullscreen event handler not available:', error);
      }
    }
  }, [handleFullscreenChange]);

  return (
    <div ref={ref} style={{ width: '100%', flex: isMobile ? '0 0 auto' : 1, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', WebkitUserSelect: 'none' }}>
      <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }} contentStyle={{ width: '100%', height: '100%' }}>
        <div style={{ overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
          {pdfDetails && scale && (
            <div style={wrapperCss}>
              <FlipbookLoader
                ref={flipbookRef}
                pdfDetails={pdfDetails}
                scale={scale}
                viewRange={viewRange}
                setViewRange={setViewRange}
                viewerStates={viewerStates}
                setViewerStates={setViewerStates}
              />
            </div>
          )}
        </div>
      </TransformComponent>
    </div>
  );
});

Flipbook.displayName = 'Flipbook';
export default Flipbook;
